import * as amplitude from "@amplitude/analytics-browser";
import { useCallback, useEffect, useRef } from "react";
import type { ExitReasonId } from "../components/ui/feedback-modal";
import { trackAuthHandoffOnce } from "../core/auth-handoff-tracking";
import { useAuth } from "../core/auth-provider";
import type { ProductEntry } from "../products";
import type { ReturnsRoutingDecision } from "../returns-routing-decision";
import { useMerchantInfo } from "./use-merchant-info";
import { useProduct } from "./use-product";

interface StepProperties {
  product_id: string;
  product_label: string;
  step_id: string;
  step_label: string;
  step_index: number;
  total_steps: number;
}

function buildStepProps(
  product: ProductEntry,
  stepId: string,
  stepIndex: number,
): StepProperties {
  const step = product.steps[stepIndex];
  return {
    product_id: product.id,
    product_label: product.label,
    step_id: stepId,
    step_label: step?.label ?? stepId,
    step_index: stepIndex,
    total_steps: product.steps.length,
  };
}

/**
 * Provides typed analytics helpers pre-populated with merchant and product context.
 * Call `useIdentifyMerchant()` once at the app shell level to link Amplitude user records
 * to the authenticated merchant.
 */
export function useIdentifyMerchant() {
  const { merchantInfo } = useMerchantInfo();

  useEffect(() => {
    if (!merchantInfo) {
      return;
    }
    amplitude.setUserId(merchantInfo.merchantId);
    amplitude.setGroup("team", merchantInfo.merchantId);

    const identifyEvent = new amplitude.Identify()
      .set("merchant_id", merchantInfo.merchantId)
      .set("name", merchantInfo.storeName)
      .set("store_name", merchantInfo.storeName)
      .set("store_url", merchantInfo.storeUrl)
      .set("email", merchantInfo.email);

    if (merchantInfo.domain) {
      identifyEvent.set("domain", merchantInfo.domain);
    }

    if (merchantInfo.myshopifyDomain) {
      identifyEvent.set("myshopify_domain", merchantInfo.myshopifyDomain);
    }

    amplitude.identify(identifyEvent);
  }, [merchantInfo]);
}

export function useTrackAuthHandoff() {
  const auth = useAuth();
  const trackedRef = useRef(false);

  useEffect(() => {
    trackAuthHandoffOnce(auth, trackedRef, amplitude.logEvent);
  }, [auth]);
}

export function useOnboardingTracking() {
  const product = useProduct();

  const trackStepCompleted = useCallback(
    (stepId: string, stepIndex: number, timeSpentMs: number) => {
      if (!product) {
        return;
      }
      amplitude.logEvent("onboarding_step_completed", {
        ...buildStepProps(product, stepId, stepIndex),
        time_spent_ms: timeSpentMs,
      });
    },
    [product],
  );

  const trackExitReason = useCallback(
    (props: {
      stepId: string;
      stepIndex: number;
      reasonId: ExitReasonId;
      reasonLabel: string;
      responseText: string;
    }) => {
      if (!product) {
        return;
      }
      amplitude.logEvent("onboarding_exit_reason", {
        ...buildStepProps(product, props.stepId, props.stepIndex),
        reason_id: props.reasonId,
        reason_label: props.reasonLabel,
        response_text: props.responseText,
      });
    },
    [product],
  );

  const trackCompleted = useCallback(() => {
    if (!product) {
      return;
    }
    amplitude.logEvent("onboarding_completed", {
      product_id: product.id,
      product_label: product.label,
      total_steps: product.steps.length,
    });
  }, [product]);

  const trackRoutingDecision = useCallback(
    (decision: ReturnsRoutingDecision) => {
      if (!product) {
        return;
      }
      amplitude.logEvent("onboarding_routing_decision", {
        product_id: product.id,
        product_label: product.label,
        decision: decision.decision,
        reason: decision.reason,
        total_annual_order_volume: decision.totalAnnualOrderVolume,
        decided_at: decision.decidedAt,
      });
    },
    [product],
  );

  return {
    trackStepCompleted,
    trackExitReason,
    trackCompleted,
    trackRoutingDecision,
  };
}
